//
// Imports
//

// Types
import type { PendingSample } from "../../../services/api/domains/pending/pending.types";

/**
 * Reject All Dialog Composable
 *
 * Confirmation dialog for rejecting all results for a sample.
 *
 * @param key - Unique key for state isolation
 */
export const useRejectAllDialog = (key: string = "default") => {
  //
  // State
  //
  const isOpen = useState<boolean>(`rejectAll.${key}.isOpen`, () => false);
  const loading = useState<boolean>(`rejectAll.${key}.loading`, () => false);
  const selectedSample = useState<PendingSample | null>(`rejectAll.${key}.selectedSample`, () => null);

  // Callback for confirm (to be set by parent)
  let confirmCallback = async (): Promise<void> => {
    console.warn("[useRejectAllDialog] confirm() not set");
  };

  //
  // Computed Properties
  //
  const sampleBarcode = computed(() => selectedSample.value?.barcode || "");
  const sampleType = computed(() => selectedSample.value?.typeName || "");

  const componentProps = computed(() => ({
    isOpen: isOpen.value,
    sampleBarcode: sampleBarcode.value,
    sampleType: sampleType.value,
    loading: loading.value,
    onConfirm: () => confirmCallback(),
    onCancel: close,
  }));

  //
  // Methods
  //

  /**
   * Open dialog with sample
   */
  const open = (sample: PendingSample): void => {
    selectedSample.value = sample;
    isOpen.value = true;
  };

  /**
   * Close dialog and reset state
   */
  const close = (): void => {
    isOpen.value = false;
    // State is intentionally not cleared here — old data stays visible during
    // the close animation. State will be replaced by the next open() call.
  };

  // Setter for parent
  const setConfirm = (fn: () => Promise<void>) => {
    confirmCallback = fn;
  };

  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    loading,
    selectedSample: readonly(selectedSample),

    // Computed
    sampleBarcode: readonly(sampleBarcode),
    componentProps,

    // Methods
    open,
    close,

    // Setter for parent
    setConfirm,
  };
};
